import axios from 'axios';
import { parseInTz } from 'helpers/date';
import { nextEventLoop } from 'helpers/timing';
import { constructURL } from 'helpers/url';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = ['previousNoteSubmitButton'];
  static values = {
    src: String,
    loading: {
      type: String,
      default: 'eager',
    },
    disciplineId: String,
    patientId: String,
    dateTimeOfService: String,
  };

  controller = new AbortController();

  initialize() {
    this.intersectionObserver = new IntersectionObserver(this.intersect.bind(this));

    if (this.loadingValue === 'eager') this.makeRequest();
    if (this.loadingValue === 'lazy') this.intersectionObserver.observe(this.element);
  }

  connect() {
    const isoDateTime = parseInTz(this.dateTimeOfService.value).format();
    this.updateChangeNoteTypeLinks(isoDateTime);
  }

  dateTimeOfServiceChanged(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const isoDateTime = parseInTz(input.value).format();

    this.updatePreviousNoteLink(isoDateTime);
    this.updateChangeNoteTypeLinks(isoDateTime);
  }

  previousNoteSubmitButtonTargetConnected(anchor) {
    const isoDateTime = parseInTz(this.dateTimeOfService.value).format();
    const url = constructURL(anchor.href, { date_time_of_service: isoDateTime });
    anchor.href = url.toString();
  }

  /**
   * Called when the history is loaded from the autosave menu.
   */
  historyLoaded() {
    const input = this.dateTimeOfService;
    if (!(input instanceof HTMLInputElement)) return;
    const isoDateTime = parseInTz(input.value).format();

    this.updatePreviousNoteLink(isoDateTime);
    this.updateChangeNoteTypeLinks(isoDateTime);
  }

  async srcValueChanged() {
    // make request only when the oldValue was present and it changed
    const skilledInterventionsSelect = this.srcValue.includes('skilled-interventions');
    const previousNote = document.getElementById('visit-note-previous-note');
    const skilledInterventions = document.getElementById('skilled-interventions-container');
    const loadingIndicator = document.getElementById('loading-indicator');
    const siLoadingIndicator = document.getElementById('si-loading-indicator');

    previousNote?.classList.add('d-none');
    skilledInterventions?.classList.add('d-none');
    loadingIndicator?.classList.remove('d-none');
    siLoadingIndicator?.classList.remove('d-none');
    await this.makeRequest(skilledInterventionsSelect);
    loadingIndicator?.classList.add('d-none');
    siLoadingIndicator?.classList.add('d-none');
    previousNote?.classList.remove('d-none');
    skilledInterventions?.classList.remove('d-none');
  }

  loadPreviousNote(event) {
    const { id, dataset } = event.detail.relatedTarget;
    this.srcValue = `/visit-notes/${id}/previous-note?discipline_id=${this.disciplineIdValue}&patient_id=${this.patientIdValue}&crud_action=${dataset.crudaction}&model_id=${dataset.modelid}&date_time_of_service=${this.dateTimeOfServiceValue}`;
  }

  loadSkilledInterventionsSelect(event) {
    const { dataset } = event.target;
    this.srcValue = `/visit-notes/skilled-interventions?care_plan_id=${event.target.value}&&crud_action=${dataset.crudaction}&model_id=${dataset.modelid}`;
  }

  async makeRequest(si_select) {
    if (!this.hasSrcValue || !this.srcValue) return;

    this.controller?.abort();
    this.controller = new AbortController();

    this.clearObserver();
    try {
      const data = await makeNetworkRequest(this.element, this.srcValue, {
        headers: {
          accept: 'text/fragment+html',
          credentials: 'same-origin',
        },
        signal: this.controller.signal,
      });

      if (si_select) {
        this.skilledInterventionsContainer.innerHTML = data;
      } else {
        this.targetContainer.innerHTML = data;
      }

      this.dispatch('success', { bubbles: true });
      this.controller = null;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.dispatch('error', { bubbles: true });
        this.controller = null;
      }
    }
  }

  // Private

  intersect(entries) {
    const lastEntry = entries.slice(-1)[0];
    if (lastEntry?.isIntersecting) {
      const { target } = lastEntry;

      this.clearObserver(target);
      this.makeRequest();
    }
  }

  clearObserver(target = this.element) {
    this.intersectionObserver?.unobserve(target);
  }

  updateChangeNoteTypeLinks(isoDateTime) {
    for (const button of document.querySelectorAll('.note_type_dropdown')) {
      const url = constructURL(button.href, { date_time_of_service: isoDateTime });
      button.href = url.toString();
    }
  }

  updatePreviousNoteLink(isoDateTime) {
    if (this.hasPreviousNoteSubmitButtonTarget) {
      const url = constructURL(this.previousNoteSubmitButtonTarget.href, { date_time_of_service: isoDateTime });
      this.previousNoteSubmitButtonTarget.href = url.toString();
    }
  }

  get targetContainer() {
    const element = document.getElementById('visit-note-previous-note');
    return element || this.element;
  }

  get skilledInterventionsContainer() {
    const element = document.getElementById('skilled-interventions-container');
    return element || this.element;
  }

  get dateTimeOfService() {
    return document.getElementById('visit_note_date_time_of_service');
  }
}

async function makeNetworkRequest(el, src, options) {
  try {
    el.dispatchEvent(new CustomEvent('loadstart'));
    const { data } = await axios.get(src, options);
    el.dispatchEvent(new CustomEvent('load'));
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    return data;
  } catch (error) {
    el.dispatchEvent(new CustomEvent('error'));
    el.dispatchEvent(new CustomEvent('loadend'));
    await nextEventLoop();
    throw error;
  }
}
